import { getAlgoliaIndexName, normalizedBrowseObjects } from "./helpers";
import { algoliasearch } from "algoliasearch";
import { useMemo } from "react";
import type { AlgoliaBrowseClient, BrowseParams } from "./types";

// Kept in its own file too, same reason as algoliaSearchClient.ts. Everything here
// (browseObjects, getObject, searchSingleIndex) needs the full SDK, since the lite build
// used for search-only stuff doesn't have these methods.
const BROWSE_API_KEY = process.env.NEXT_PUBLIC_ALGOLIA_BROWSE_API_KEY || "";
const SEARCH_API_KEY = process.env.NEXT_PUBLIC_ALGOLIA_API_KEY || "";
const APP_ID = process.env.NEXT_PUBLIC_ALGOLIA_APP_ID || "";

const browseClientCache = new Map<string, AlgoliaBrowseClient>();

const getOrCreateBrowseClient = (apiKey: string, indexName: string): AlgoliaBrowseClient => {
    const cacheKey = `${apiKey}-${indexName}`;

    if (!browseClientCache.has(cacheKey)) {
        const client = algoliasearch(APP_ID, apiKey);

        browseClientCache.set(cacheKey, {
            ...client,
            // Wrap browse in our own callback shape and bind it to this index, so
            // callers depend on our contract instead of the SDK's own signature.
            browseObjects: (params: BrowseParams) =>
                normalizedBrowseObjects(client, indexName, params),
        } as unknown as AlgoliaBrowseClient);
    }

    return browseClientCache.get(cacheKey)!;
};

export const algoliaBrowse = (
    locale: string,
    apiKey = BROWSE_API_KEY,
    mode: "products" | "stores" = "products"
): AlgoliaBrowseClient => {
    return getOrCreateBrowseClient(apiKey, getAlgoliaIndexName(locale, mode));
};

// getObject and getObjects need the search permission, which the browse key does not have.
export const algoliaObjects = (locale: string): AlgoliaBrowseClient =>
    algoliaBrowse(locale, SEARCH_API_KEY);

export const useAlgoliaBrowseClient = (
    locale: string,
    mode: "products" | "stores" = "products"
): { browseClient: AlgoliaBrowseClient; indexName: string } =>
    useMemo(
        () => ({
            browseClient: algoliaBrowse(locale, BROWSE_API_KEY, mode),
            indexName: getAlgoliaIndexName(locale, mode),
        }),
        [locale, mode]
    );
